import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { FilterEvent } from '../types/filterEvent';
import EnButton from 'Components/base/EnButton/EnButton.vue';
import { EnMenu } from 'Components/base/EnMenu';
import { ChevronDown, X } from 'lucide-vue-next';
import { buildStaticId } from 'Components/utils/buildStaticId';
/**
 * Base component for EnFilter buttons.
 */
const nestedOverlaySelector = '[data-slot="popover-content"], [data-slot="select-content"]';
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnFilterButtonBase',
    props: /*@__PURE__*/ _mergeModels({
        onResetButtonClicked: Function,
        applyFilters: Function,
        resetButtonText: String,
        applyButtonText: String,
        displayString: [Number, String],
        label: String,
        hideApplyButton: Boolean,
        resetFilter: Function,
        id: String,
        searchInputClass: [String, Object, Array],
        isActionButtonsRendered: {
            type: Boolean,
            default: true,
        },
    }, {
        "modelValue": {},
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(['update:modelValue', 'click', FilterEvent.Input], ["update:modelValue"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const props = __props;
        const modelValue = _useModel(__props, "modelValue");
        const menuComponent = ref();
        // Keeps the menu below the fixed navigation bar ($navigation-bar-height + gap).
        const collisionPadding = { top: 64, right: 8, bottom: 8, left: 8 };
        const emit = __emit;
        let lastPointerDownWasInNestedOverlay = false;
        /**
         * Emits the resetFilter function if it exists.
         * Closes the menu after resetting the filter.
         */
        function emitOnResetButtonClicked() {
            if (props.onResetButtonClicked) {
                props.onResetButtonClicked();
            }
            closeMenu();
        }
        /**
         * Emits the applyFilters function if it exists.
         * Closes the menu after applying filters.
         */
        function emitApplyFilters() {
            if (props.applyFilters) {
                props.applyFilters();
            }
            closeMenu();
        }
        /**
         * Handles the click event on the filter button.
         * Emits a 'click' event to notify parent components.
         * @param {Event} event - The click event.
         */
        function handleClick(event) {
            emit('click', event);
        }
        function onDocumentPointerDown(event) {
            lastPointerDownWasInNestedOverlay = Boolean(event?.target?.closest?.(nestedOverlaySelector));
        }
        /**
         * Close the menu on outside-click, but ignore clicks that land inside
         * a reka-ui Popover (e.g. the date-picker calendar teleported to body).
         */
        function onClickOutside(event) {
            const startedInNestedOverlay = lastPointerDownWasInNestedOverlay;
            lastPointerDownWasInNestedOverlay = false;
            if (startedInNestedOverlay) {
                return;
            }
            const target = event?.target;
            // ToDo: probably not needed anymore
            // Vuetify invokes this handler asynchronously. An adjacent-month selection can
            // detach the calendar cell before then, so its popover ancestry is no longer available.
            const isCalendarDateSelection = target?.matches?.('[data-reka-calendar-cell-trigger]');
            if (isCalendarDateSelection || target?.closest?.('[data-slot="popover-content"]')) {
                return;
            }
            closeMenu();
        }
        function closeMenu() {
            modelValue.value = false;
            menuComponent.value.setOpen(false);
        }
        watch(modelValue, (newVal) => {
            menuComponent.value.setOpen(newVal);
        });
        onMounted(() => {
            menuComponent.value.setOpen(modelValue.value);
            document.addEventListener('pointerdown', onDocumentPointerDown, true);
        });
        onBeforeUnmount(() => {
            document.removeEventListener('pointerdown', onDocumentPointerDown, true);
        });
        const __returned__ = { props, modelValue, menuComponent, collisionPadding, emit, nestedOverlaySelector, get lastPointerDownWasInNestedOverlay() { return lastPointerDownWasInNestedOverlay; }, set lastPointerDownWasInNestedOverlay(v) { lastPointerDownWasInNestedOverlay = v; }, emitOnResetButtonClicked, emitApplyFilters, handleClick, onDocumentPointerDown, onClickOutside, closeMenu, EnButton, get EnMenu() { return EnMenu; }, get ChevronDown() { return ChevronDown; }, get X() { return X; }, get buildStaticId() { return buildStaticId; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
